import React, { useEffect } from 'react';
import { Navigate, useNavigate } from 'react-router-dom';
import { useSelector } from 'react-redux';
import Loader from '../components/common/Loader';
import { useGetAllModules } from '../features/master/hooks/useModuleAccess';
// import { useGetkey } from '../features/master/hooks/usekey';
import { useQueryClient } from '@tanstack/react-query';

const useAuth = () => {
	const { auth, isLoading } = useSelector((state) => state.auth);
	return { isLoading, auth };
};

// export const ProtectedRoute = ({ children }) => {
// 	const { isLoading, auth } = useAuth();

// 	const { data: key, isFetched: keyFetch } = useGetkey({});
// 	const keyData = key?.key;

// 	if (isLoading) {
// 		return (
// 			<div className='fixed inset-0 flex items-center justify-center bg-gray-100 bg-opacity-50 z-50'>
// 				<Loader />
// 			</div>
// 		);
// 	}

// 	if (!auth) {
// 		return <Navigate to='/login' replace />;
// 	}

// 	// 🚫 KEY VALIDATION (Important)
// 	if (keyFetch && keyData === false && window.location.pathname !== '/license') {
// 		return <Navigate to='/license' replace />;
// 	}

// 	return children;
// };

export const ProtectedRoute = ({ children }) => {
	const { isLoading, auth } = useAuth();
	const navigate = useNavigate();
	const queryClient = useQueryClient();
	const userType = auth?.userType?.toUpperCase();
	const isAdmin = userType === 'SUPER ADMIN' || userType === 'ADMIN';

	// ✅ Just read from cache — no API call, Layout keeps it fresh
	const cachedKey = queryClient.getQueryData(['license']);
	const keyData = cachedKey?.key;
	const keyFetch = cachedKey !== undefined;

	// ✅ Force re-check on route change by invalidating cache
	useEffect(() => {
		queryClient.invalidateQueries(['license']);
	}, [window.location.pathname]);

	// Redirect immediately if license becomes invalid mid-session
	useEffect(() => {
		if (!keyFetch || isAdmin) return;
		if (keyData === false && window.location.pathname !== '/license') {
			navigate('/license', { replace: true });
		}
	}, [keyData, keyFetch, isAdmin]);

	// Wait for auth + opening balance
	if (isLoading) {
		return (
			<div className='fixed inset-0 flex items-center justify-center bg-gray-100 bg-opacity-50 z-50'>
				<Loader />
			</div>
		);
	}

	// Not logged in
	if (!auth) {
		return <Navigate to='/login' replace />;
	}

	// License expired
	if (!isAdmin && keyFetch && keyData === false && window.location.pathname !== '/license') {
		return <Navigate to='/license' replace />;
	}

	return children;
};

export const ProtectedApprovalRoutes = ({ children }) => {
	const { isLoading, auth } = useAuth();
	const UserRole = auth?.UserRole;

	if (isLoading) {
		return (
			<div className='fixed inset-0 flex items-center justify-center bg-gray-100 bg-opacity-50 z-50'>
				<Loader />
			</div>
		);
	}

	if (UserRole === '47') {
		return <Navigate to='/' replace />;
	}

	return children;
};

export const ProtectedAdminRoutes = ({ children }) => {
	const { isLoading, auth } = useAuth();
	const role = auth?.role;

	if (isLoading) {
		return (
			<div className='fixed inset-0 flex items-center justify-center bg-gray-100 bg-opacity-50 z-50'>
				<Loader />
			</div>
		);
	}

	if (role !== 'ADMIN') {
		return <Navigate to='/' replace />;
	}

	return children;
};

export const ProtectedModuleRoutes = ({ children, requiredModule, isHistory = false }) => {
	const { isLoading, auth } = useAuth();
	const ModuleAccess = auth?.moduleAccess;
	const userRole = auth?.role;

	const { data: allModules } = useGetAllModules();
	// Convert module access string into an array of strings
	const accessibleModules = ModuleAccess?.split(',') || [];

	const requiredIds = Array.isArray(requiredModule)
		? requiredModule
				.map((name) => {
					const match = allModules?.data?.find((mod) => mod.ModuleName === name);
					return match?.ModuleId;
				})
				.filter(Boolean)
		: (() => {
				const match = allModules?.data?.find((mod) => mod.ModuleName === requiredModule);
				return match ? [match.ModuleId] : [];
			})();

	// Check if user has access
	const hasAccess = Array.isArray(requiredIds)
		? requiredIds.some((module) => accessibleModules.includes(String(module)))
		: accessibleModules.includes(String(requiredIds));

	// Special rule: block module 7 unless user is ADMIN or it's history
	const isSpecial7Blocked = Array.isArray(requiredIds)
		? requiredIds.includes('7') || requiredIds.includes(7)
		: requiredIds === '7' || requiredIds === 7;

	if (isLoading || !auth || !allModules?.data) {
		return (
			<div className='fixed inset-0 flex items-center justify-center bg-gray-100 bg-opacity-50 z-50'>
				<Loader />
			</div>
		);
	}

	if (!auth || !hasAccess || (isSpecial7Blocked && !isHistory && userRole !== 'ADMIN')) {
		return <Navigate to='/' replace />;
	}

	return children;
};
